import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import {
  NotificationDTO,
  SmartSuggestionNotificationContext,
} from '../../dtos/notifications.dto';
import {
  NOTIFICATION_TYPE_META,
  notificationLink,
  notificationTitle,
} from '../../helpers/notification-type.helpers';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { UserPusherService } from '../../services/pusher/user.pusher.service';
import { QuickWalkService } from '../../services/quick-walk/quick-walk.service';
import { AvatarDTO } from '../avatar/avatar.dto';

const RECENT_WINDOW_DAYS = 30;
const BELL_FETCH_PER_PAGE = 50;

const groupLabelFor = (notification: NotificationDTO): string => {
  const date = DateTime.fromSeconds(notification.notification_date);
  const now = DateTime.now();

  if (date.hasSame(now, 'day')) {
    return 'Today';
  }
  if (date.hasSame(now.minus({ days: 1 }), 'day')) {
    return 'Yesterday';
  }
  if (date >= now.minus({ days: 7 })) {
    return 'Last week';
  }
  return 'Older';
};

const trackByGroupLabel = (_: number, group: { label: string }): string =>
  group.label;

const trackByNotificationId = (
  _: number,
  notification: NotificationDTO
): number => notification.id;

@Component({
  selector: 'app-notifications-bell',
  templateUrl: './notifications-bell.component.html',
  styleUrl: './notifications-bell.component.scss',
  standalone: false,
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
  @ViewChild('notificationsDropdown') notificationsDropdown: NgbDropdown;

  notifications: NotificationDTO[] = [];

  typeMeta = NOTIFICATION_TYPE_META;

  notificationTitle = notificationTitle;

  private notificationCreatedSubscription: Subscription;

  constructor(
    private notificationsService: NotificationsService,
    private userPusherService: UserPusherService,
    private quickWalkService: QuickWalkService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchNotifications();

    this.notificationCreatedSubscription =
      this.userPusherService.notificationCreatedNotifier.subscribe(
        (notification) => {
          this.notifications = [notification, ...this.notifications];
        }
      );
  }

  ngOnDestroy(): void {
    this.notificationCreatedSubscription?.unsubscribe();
  }

  fetchNotifications(): void {
    this.notificationsService
      .getNotifications(false, undefined, { per_page: BELL_FETCH_PER_PAGE })
      .subscribe((res) => {
        this.notifications = res?.items || [];
      });
  }

  onDropdownOpenChange(open: boolean): void {
    if (open) {
      this.markListedAsRead();
    }
  }

  goToAllNotifications(): void {
    this.notificationsDropdown.close();
    this.router.navigate(['/notifications']);
  }

  goToNotificationSource(notification: NotificationDTO): void {
    const ctx = notification.notification_context as
      | SmartSuggestionNotificationContext
      | null
      | undefined;
    if (ctx?.action === '#quickWalkModal') {
      this.notificationsDropdown.close();
      this.quickWalkService.openQuickWalk();
      return;
    }

    const link = notificationLink(notification);
    if (!link) {
      return;
    }
    this.notificationsDropdown.close();
    this.router.navigateByUrl(link);
  }

  expandedIds = new Set<number>();

  toggleExpand(id: number, event: Event): void {
    event.stopPropagation();
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  messageIsLong(message: string): boolean {
    return message.length > 100;
  }

  // groupedNotifications is a getter recomputed on every change-detection cycle — without
  // trackBy, *ngFor sees new object/array references every time and tears down + recreates
  // the DOM on each cycle, which can drop a click mid-event before it's handled.
  trackByGroupLabel = trackByGroupLabel;

  trackByNotificationId = trackByNotificationId;

  getActorAvatarData(notification: NotificationDTO): AvatarDTO | null {
    if (notification.actorUser) return null;
    const url = this.typeMeta[notification.notification_type]?.avatarUrl;
    return url
      ? { firstName: '', lastName: '', color: 'transparent', photoUrl: url }
      : null;
  }

  get notificationsWithinWindow(): NotificationDTO[] {
    const cutoff = DateTime.now()
      .minus({ days: RECENT_WINDOW_DAYS })
      .toSeconds();
    return this.notifications.filter((n) => n.notification_date >= cutoff);
  }

  get unreadCount(): number {
    return this.notificationsWithinWindow.filter((n) => !n.date_shown).length;
  }

  get groupedNotifications(): { label: string; items: NotificationDTO[] }[] {
    const order = ['Today', 'Yesterday', 'Last week', 'Older'];
    const buckets = new Map<string, NotificationDTO[]>();

    this.notificationsWithinWindow.forEach((notification) => {
      const label = groupLabelFor(notification);
      if (!buckets.has(label)) {
        buckets.set(label, []);
      }
      buckets.get(label)?.push(notification);
    });

    return order
      .filter((label) => buckets.has(label))
      .map((label) => ({
        label,
        items: buckets.get(label) as NotificationDTO[],
      }));
  }

  private markListedAsRead(): void {
    const ids = this.notificationsWithinWindow.map((n) => n.id);
    if (!ids.length) {
      return;
    }

    this.notificationsService.markNotificationAsRead(ids).subscribe(() => {
      const now = Math.floor(Date.now() / 1000);
      this.notifications.forEach((n) => {
        if (ids.includes(n.id)) {
          n.date_shown = now;
        }
      });
    });
  }
}
